import NoPhotoImg from "public/images/no-photo.png";

import { executeIfDefined } from "@/utils/function.utils";
import { makeUrl } from "@/utils/url-utils/urlMaker";

// TODO: illuminate duplicate types and params

export type LinkAssemblersFunctionParams = {
  mediaName: string;
  isPrivatePhoto?: boolean;
};

export type SafeLinkAssemblerFunctionParams = {
  mediaName?: string;
  isPrivatePhoto?: boolean;
};

const getPrefixFromMediaName = (mediaName: string) => {
  return mediaName.slice(0, 3).split("").join("/");
};

type makeCloudFrontUrlParams = {
  mediaName: string;
  postfix: string;
};

const makeCloudFrontUrl = ({ mediaName, postfix }: makeCloudFrontUrlParams) => {
  return makeUrl(process.env.CLOUDFRONT_LINK, "lady", mediaName, postfix);
};

type makeServicesUrlParams = {
  mediaName: string;
  postfix: string;
  isPrivatePhoto?: boolean;
};

const makeServicesUrl = ({
  mediaName,
  postfix,
  isPrivatePhoto,
}: makeServicesUrlParams) => {
  return makeUrl(
    process.env.ASSETS_BASE_URL,
    `goldenbride/services/photo?/${isPrivatePhoto ? "private" : "ladyp"}/`,
    mediaName,
    postfix,
  );
};

const withSafeExecute = <
  F extends (arg: P) => string,
  P extends LinkAssemblersFunctionParams,
>(
  fn: F,
) => {
  const picturePlug = (NoPhotoImg as StaticAssetWithSrc).src;

  return (args: P) => {
    return executeIfDefined(fn, args, picturePlug);
  };
};

const isCloudFront = process.env.IS_ASSETS_MEDIA !== "true";

export const linkAssemblers = {
  makePhotoUrl: (
    params: LinkAssemblersFunctionParams & { privatePhoto?: boolean },
  ) => {
    const { mediaName, isPrivatePhoto } = params;

    if (isCloudFront) {
      return makeCloudFrontUrl({
        mediaName,
        postfix: "photo.jpg",
      });
    }

    return makeServicesUrl({
      mediaName,
      postfix: "photo",
      isPrivatePhoto: isPrivatePhoto,
    });
  },

  makePhotoThumbnailMedium: (
    params: LinkAssemblersFunctionParams & { isPrivatePhoto?: boolean },
  ) => {
    const { mediaName, isPrivatePhoto } = params;

    if (isCloudFront) {
      return makeCloudFrontUrl({
        mediaName,
        postfix: "thumbnailMedium.jpg",
      });
    }

    return makeServicesUrl({
      mediaName,
      postfix: "thumbnailMedium",
      isPrivatePhoto: isPrivatePhoto,
    });
  },

  makePhotoThumbnailSmall: (
    params: LinkAssemblersFunctionParams & { isPrivatePhoto?: boolean },
  ) => {
    const { mediaName, isPrivatePhoto } = params;

    if (isCloudFront) {
      return makeCloudFrontUrl({
        mediaName,
        postfix: "thumbnailSmall.jpg",
      });
    }

    return makeServicesUrl({
      mediaName,
      postfix: "thumbnailSmall",
      isPrivatePhoto: isPrivatePhoto,
    });
  },

  safeMakePhotoUrl: function (params: SafeLinkAssemblerFunctionParams) {
    if (!params.mediaName) return NoPhotoImg.src;

    return withSafeExecute(this.makePhotoUrl)({
      ...params,
      mediaName: params.mediaName,
      isPrivatePhoto: params.isPrivatePhoto,
    });
  },

  safeMakePhotoThumbnailMedium: function (
    params: SafeLinkAssemblerFunctionParams,
  ) {
    if (!params.mediaName) return NoPhotoImg.src;

    return withSafeExecute(this.makePhotoThumbnailMedium)({
      ...params,
      mediaName: params.mediaName,
      isPrivatePhoto: params.isPrivatePhoto,
    });
  },

  safeMakePhotoThumbnailSmall: function (
    params: SafeLinkAssemblerFunctionParams,
  ) {
    if (!params.mediaName) return NoPhotoImg.src;

    return withSafeExecute(this.makePhotoThumbnailSmall)({
      ...params,
      mediaName: params.mediaName,
      isPrivatePhoto: params.isPrivatePhoto,
    });
  },

  makeVideoSourceUrl: (mediaName: string) => {
    const pathNumberPrefix = getPrefixFromMediaName(mediaName);
    return `${process.env.GB_API_BASE_URL}goldenbride/services/video?/${pathNumberPrefix}/${mediaName}`;
  },
  makeVideoStaticUrl: (mediaName: string) => {
    const pathNumberPrefix = getPrefixFromMediaName(mediaName);
    return `${process.env.GB_API_BASE_URL}goldenbride/services/video?/${pathNumberPrefix}/${mediaName}_static`;
  },
  makeVideoGifUrl: (mediaName: string) => {
    const pathNumberPrefix = getPrefixFromMediaName(mediaName);
    return `${process.env.GB_API_BASE_URL}goldenbride/services/video?/${pathNumberPrefix}/${mediaName}_gif`;
  },
};

linkAssemblers.safeMakePhotoUrl =
  linkAssemblers.safeMakePhotoUrl.bind(linkAssemblers);
linkAssemblers.safeMakePhotoThumbnailMedium =
  linkAssemblers.safeMakePhotoThumbnailMedium.bind(linkAssemblers);
linkAssemblers.safeMakePhotoThumbnailSmall =
  linkAssemblers.safeMakePhotoThumbnailSmall.bind(linkAssemblers);
